import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box, Dialog, DialogContent,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  DialogTitle,
  FormControl,
  Stack,
  Divider
} from '@mui/material';

import { Close, KeyboardArrowDownOutlined } from '@mui/icons-material';

import {
  communicationMethodOptions,
  contactOutcomeOptions,
  patientFallback
} from './NotifyPatientModalData';

import {
  metaTextSx,
  providerResponseSectionSx, recommendationSelectSx
} from '../../Common/components/Styles/CommunicationDialogStyles';
import {
  menuProps,
  textFieldSx, dialogHeaderStyles,
  patientNameSx,
  patientMrSx,
  patientBoxSx,
  sectionTitleSx,
  issueTextSx
} from '../../Common/components/Styles/InputStyles';
import CommonDialogActions from '../../Common/components/CommonDialogActions';

const NotifyPatientModal = ({ open, onClose, data }) => {
  const [communicationMethod, setCommunicationMethod] = useState('');
  const [contactOutcome, setContactOutcome] = useState('');

  useEffect(() => {
    if (!open) {
      setCommunicationMethod('');
      setContactOutcome('');
    }
  }, [open]);

  const patientData = {
    ...patientFallback,
    ...data
  };

  const isValid = Boolean(communicationMethod && contactOutcome);

  const handleSubmit = () => {
    if (!isValid) {
      return;
    }

    const submitData = {
      communicationMethod,
      contactOutcome,
      patient: patientData
    };

    console.log(submitData);

    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px'
        }
      }}>
      <DialogTitle sx={dialogHeaderStyles.dialogheader}>
        <Box>
          <Typography sx={dialogHeaderStyles.title}>Notifications</Typography>

          <Typography sx={dialogHeaderStyles.subtitle}>
            {' '}
            Send notifications and updates to the patient.
          </Typography>
        </Box>

        <IconButton
          aria-label="Close create issue dialog"
          onClick={onClose}
          size="small"
          sx={dialogHeaderStyles.dialogheaderclose}>
          <Close sx={{ fontSize: 22 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2, my: 2 }}>
        <Box sx={patientBoxSx}>
          <Typography sx={patientNameSx}>{patientData.patient}</Typography>

          <Typography sx={patientMrSx}>MR# {patientData.mrNumber}</Typography>
        </Box>

        <Typography sx={sectionTitleSx}>Issue Note</Typography>

        <Typography sx={metaTextSx}>
          {patientData.createdBy}
          {' \u2022 '}
          {patientData.createdDate}
        </Typography>

        <Typography
          sx={{
            ...metaTextSx,
            mb: 2
          }}>
          Category: {patientData.category || '-'}
          {' \u2022 '}
          Priority: {patientData.priority || '-'}
        </Typography>

        <Typography sx={issueTextSx}>{patientData.issueNote}</Typography>

        <Box sx={providerResponseSectionSx}>
          <Typography sx={sectionTitleSx}>Provider Response</Typography>

          <Typography sx={metaTextSx}>
            {patientData.providerName}
            {' \u2022 '}
            {patientData.providerResponseDate}
          </Typography>

          <Typography sx={{ ...issueTextSx, pt: 1 }}>
            {patientData.providerResponse}
          </Typography>
        </Box>
        <Stack spacing={2}>
          <Divider />

          <FormControl fullWidth size="small" sx={textFieldSx}>
            <InputLabel id="communication-method-label">
              Communication Method
            </InputLabel>

            <Select
              labelId="communication-method-label"
              id="communication-method"
              value={communicationMethod}
              label="Communication Method"
              IconComponent={KeyboardArrowDownOutlined}
              onChange={event => setCommunicationMethod(event.target.value)}
              MenuProps={menuProps}>
              {communicationMethodOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={textFieldSx}>
            <InputLabel id="contact-outcome-label">Contact Outcome</InputLabel>

            <Select
              labelId="contact-outcome-label"
              IconComponent={KeyboardArrowDownOutlined}
              id="contact-outcome"
              value={contactOutcome}
              label="Contact Outcome"
              onChange={event => setContactOutcome(event.target.value)}
              MenuProps={menuProps}
              sx={recommendationSelectSx}>
              {contactOutcomeOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <CommonDialogActions
        onCancel={onClose}
        onSubmit={handleSubmit}
        submitText="Mark Complete"
        disabled={!isValid}
      />
    </Dialog>
  );
};

NotifyPatientModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.shape({
    patient: PropTypes.string,
    mrNumber: PropTypes.string,
    category: PropTypes.string,
    priority: PropTypes.string,
    createdBy: PropTypes.string,
    createdDate: PropTypes.string,
    issueNote: PropTypes.string,
    providerName: PropTypes.string,
    providerResponseDate: PropTypes.string,
    providerResponse: PropTypes.string
  })
};

NotifyPatientModal.defaultProps = {
  data: null
};

export default NotifyPatientModal;
